import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Box, Grid, CircularProgress, Alert, Fade, Grow, ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourses } from '../store/thunks/coursesThunks';
import CourseCard from '../components/courses/CourseCard';
import CategoryFilter from '../components/courses/CategoryFilter';
import AdSenseBanner from '../components/AdSenseBanner';
import SEO from '../components/SEO';
import BookIcon from '@mui/icons-material/Book';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import ScheduleIcon from '@mui/icons-material/Schedule';

type SortOption = 'latest' | 'popular' | 'rated';

const CoursesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { courses, loading, error, selectedCategory } = useAppSelector((state) => state.courses);
  const [sortBy, setSortBy] = useState<SortOption>('latest');

  // Fetch courses from Firestore on mount
  useEffect(() => {
    if (courses.length === 0) {
      dispatch(fetchCourses());
    }
  }, [dispatch, courses.length]);

  // Filter and sort courses
  const filteredCourses = useMemo(() => {
    let filtered = courses;
    
    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((course) => course.category === selectedCategory);
    }
    
    // Sort courses
    const sorted = [...filtered];
    switch (sortBy) {
      case 'latest':
        sorted.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA; // Newest first
        });
        break;
      case 'popular':
        sorted.sort((a, b) => {
          const totalA = (a.totalRatings || 0) + (a.totalComments || 0);
          const totalB = (b.totalRatings || 0) + (b.totalComments || 0);
          return totalB - totalA; // Most popular first
        });
        break;
      case 'rated':
        sorted.sort((a, b) => {
          const ratingA = a.averageRating || 0;
          const ratingB = b.averageRating || 0;
          if (ratingB !== ratingA) {
            return ratingB - ratingA; // Highest rated first
          }
          // If ratings are equal, sort by number of ratings
          return (b.totalRatings || 0) - (a.totalRatings || 0);
        });
        break;
    }
    
    return sorted;
  }, [courses, selectedCategory, sortBy]);

  const handleSortChange = (_event: React.MouseEvent<HTMLElement>, newSort: SortOption | null) => {
    if (newSort !== null) {
      setSortBy(newSort);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="h6" color="text.secondary">
            Loading courses...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}
          >
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  const siteUrl = 'https://learn2skill.com';
  const coursesStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Programming Courses - Learn2Skill',
    description: 'Browse comprehensive programming courses in JavaScript, TypeScript, Python, C#, Java, React, Vue, Angular, and more. Free online courses with interactive quizzes and expert-led content.',
    url: `${siteUrl}/courses`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: courses.length,
      itemListElement: filteredCourses.slice(0, 10).map((course, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Course',
          name: course.title,
          url: `${siteUrl}/courses/${course.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title="Free Programming Courses - Learn JavaScript, Python, React & More | Learn2Skill"
        description="Browse comprehensive programming courses in JavaScript, TypeScript, Python, C#, Java, React, Vue, Angular, and more. Free online courses with interactive quizzes, hands-on projects, and expert-led tutorials."
        keywords="programming courses, coding courses, learn JavaScript, learn Python, learn React, web development courses, software engineering courses, free programming courses, online coding courses, programming tutorials"
        url="/courses"
        type="website"
        structuredData={coursesStructuredData}
      />
      <Box>
        {/* Header Section */}
        <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          py: 8,
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  mb: 3,
                }}
              >
                <BookIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Explore Our Courses
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                Master programming languages with our comprehensive, hands-on courses designed for all skill levels
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Main Content with AdSense Banners */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={2}>
          {/* Left AdSense Banner */}
          <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <AdSenseBanner
              slot="1234567890"
              format="vertical"
              position="left"
            />
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            {/* Filter Section */}
            <Box sx={{ mb: 5 }}>
              <Fade in timeout={1000}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    background: 'rgba(30, 41, 59, 0.5)',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                  }}
                >
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Filter by Category:
                  </Typography>
                </Box>
              </Fade>
              <CategoryFilter />
              
              {/* Sort Options */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                  Sort by:
                </Typography>
                <ToggleButtonGroup
                  value={sortBy}
                  exclusive
                  onChange={handleSortChange}
                  aria-label="sort options"
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: 'text.secondary',
                      borderColor: 'rgba(148, 163, 184, 0.3)',
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="latest" aria-label="latest">
                    <ScheduleIcon sx={{ mr: 1 }} />
                    Latest
                  </ToggleButton>
                  <ToggleButton value="popular" aria-label="most popular">
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Most Popular
                  </ToggleButton>
                  <ToggleButton value="rated" aria-label="most rated">
                    <StarIcon sx={{ mr: 1 }} />
                    Most Rated
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Courses Grid */}
            {filteredCourses.length === 0 ? (
              <Fade in timeout={800}>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    px: 4,
                    borderRadius: 3,
                    background: 'rgba(30, 41, 59, 0.3)',
                    border: '1px dashed rgba(148, 163, 184, 0.2)',
                  }}
                >
                  <BookIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No courses found
                  </Typography>
                  <Typography variant="body1" color="text.disabled">
                    {selectedCategory
                      ? `No courses available in the "${selectedCategory}" category.`
                      : 'No courses available at the moment.'}
                  </Typography>
                </Box>
              </Fade>
            ) : (
              <>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" color="text.secondary">
                    {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'} Available
                    {selectedCategory && ` in ${selectedCategory}`}
                  </Typography>
                </Box>
                <Grid container spacing={4}>
                  {filteredCourses.map((course, index) => (
                    <Grid item xs={12} sm={6} md={4} key={course.id}>
                      <Grow in timeout={800 + index * 100}>
                        <Box>
                          <CourseCard course={course} />
                        </Box>
                      </Grow>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Grid>

          {/* Right AdSense Banner */}
          <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <AdSenseBanner
              slot="0987654321"
              format="vertical"
              position="right"
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
    </>
  );
};

export default CoursesPage;

